import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import eventBus from '@sas/nova-core/eventBus';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { setAlpha } from '../../design-tokens';
import { useOnOutsideMouseDown } from '../../hooks/useOnOutsideMouseDown';
import { PopoverRootProvider, usePopoverRoot } from '../../hooks/usePopoverRoot';
import { renderInlineCssVars, useDesignTokens, useGlobalDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { ModalA11YProvider, useModalA11YContext, useModalSiblingCountContext } from './ModalA11YProvider';
import cn from './Modal.css.json';
import './Modal.css';

// Store this object to make sure FocusLock's returnFocus prop doesn't change when re-rendered
// Otherwise, it will trigger unexpected focus() calls.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const focusLockFocusOptions = {
  preventScroll: true
};

/**
 * Used to fire custom Modal Events
 * for now we just have one event "modal:disableFocusLockNonModal"
 */
export const publishModalEvent = eventName => {
  eventBus.publish(eventName);
};
const subscribeModalEvent = (eventName, listener) => {
  eventBus.subscribe(eventName, listener);
};
const unsubscribeModalEvent = (eventName, listener) => {
  eventBus.unsubscribe(eventName, listener);
};
export const Modal = /*#__PURE__*/forwardRef(({
  showBlockLayer = true,
  hideAnimation = false,
  isOpen,
  ...props
}, ref) => {
  const dt = useDesignTokens();
  const globalDesignTokens = useGlobalDesignTokens();
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true' && !hideAnimation;
  const cssVars = renderInlineCssVars({
    bg: setAlpha(dt.c.black, 0.25),
    'anim-dur-enter': dt.tokens['duration-300'],
    'anim-tf-enter': dt.tokens['ease-out'],
    'anim-dur-exit': dt.tokens['duration-300'],
    'anim-tf-exit': dt.tokens['ease-in']
  }, 'modal');
  const popoverRoot = usePopoverRoot();
  const [childPopoverRoot, setChildPopoverRoot] = useState(null);
  const [isFocusLockDisabled, disableFocusLock] = useState(false);
  const modalDivRef = useRef(null);
  const onOutsideClickHandler = useCallback(() => isOpen && disableFocusLock(!showBlockLayer), [showBlockLayer, isOpen]);
  useOnOutsideMouseDown(modalDivRef, onOutsideClickHandler, true);
  useEffect(() => {
    // if showBlockLayer is false this is a non-modal
    if (!showBlockLayer) {
      const disableFocusLockNonModal = () => {
        isOpen && disableFocusLock(true);
      };
      subscribeModalEvent('modal:disableFocusLockNonModal', disableFocusLockNonModal);
      return () => {
        unsubscribeModalEvent('modal:disableFocusLockNonModal', disableFocusLockNonModal);
      };
    }
  }, [showBlockLayer, isOpen]);
  const publishOpenState = useModalA11YContext();

  // handle aria-hidden when dialog rendered in DOM as siblings
  const {
    siblingCount,
    setSiblingCount
  } = useModalSiblingCountContext();
  const siblingIndex = useRef(-1);
  useEffect(() => {
    if (isOpen) {
      setSiblingCount(count => {
        siblingIndex.current = count + 1;
        return count + 1;
      });
      return () => {
        setSiblingCount(count => {
          siblingIndex.current = count - 1;
          return count - 1;
        });
      };
    }
  }, [setSiblingCount, isOpen]);
  useEffect(() => {
    if (showBlockLayer) {
      publishOpenState(isOpen);
      return () => {
        // this is specifically for the BaseDialog implementations (FullScreenDialog)
        // which unmounts the BaseDialog on closing.
        publishOpenState(false);
      };
    }
  }, [isOpen, showBlockLayer, publishOpenState]);
  const duration = shouldPlayAnimation ? 0.3 : 0;
  const modalAnimation = {
    visible: {
      opacity: 1,
      transition: {
        duration,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    hidden: {
      opacity: 0,
      transition: {
        duration,
        ease: [0, 0.5, 0.2, 1]
      }
    }
  };
  if (popoverRoot === null) {
    return null;
  } else {
    return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsx(AnimatePresence, {
      mode: "wait",
      children: isOpen ? /*#__PURE__*/_jsxs(motion.div, {
        initial: "hidden",
        animate: "visible",
        exit: "hidden",
        variants: modalAnimation,
        ...(shouldPlayAnimation ? props.animation : null),
        className: cx(cn.container, showBlockLayer ? cn.modalBlockLayer : cn.nonmodalBlockLayer),
        style: {
          ...cssVars,
          ...globalDesignTokens
        },
        onContextMenu: props.onContextMenu,
        ref: el => {
          setRef(modalDivRef, el);
          setRef(ref, el);
        },
        ...(!showBlockLayer && {
          onFocus: () => disableFocusLock(false),
          onMouseDown: () => disableFocusLock(false)
        }),
        "data-testid": props.testId,
        id: props.id,
        "data-is-modal": showBlockLayer ? true : undefined,
        ...(siblingCount > 1 && {
          'aria-hidden': siblingIndex.current === siblingCount ? false : true
        }),
        children: [/*#__PURE__*/_jsx(PopoverRootProvider, {
          value: showBlockLayer ? childPopoverRoot : popoverRoot,
          children: /*#__PURE__*/_jsx(FocusLock, {
            ...(!showBlockLayer && {
              className: cn.nonmodalBlockContent
            }),
            ...(!showBlockLayer && {
              disabled: isFocusLockDisabled
            }),
            returnFocus: focusLockFocusOptions,
            ...props.focusLockProps,
            children: showBlockLayer ? /*#__PURE__*/_jsx(ModalA11YProvider, {
              children: props.children
            }) : props.children
          })
        }), showBlockLayer ? /*#__PURE__*/_jsx("div", {
          ref: setChildPopoverRoot
        }) : null]
      }) : null
    }), popoverRoot);
  }
});
//# sourceMappingURL=Modal.js.map